export const ROOM_LOOKUP_ERROR_CODE = {
  ROOM_NOT_FOUND: 'ROOM_NOT_FOUND',
  ROOM_LOOKUP_FAILED: 'ROOM_LOOKUP_FAILED',
} as const

export type RoomLookupErrorCode =
  (typeof ROOM_LOOKUP_ERROR_CODE)[keyof typeof ROOM_LOOKUP_ERROR_CODE]

/**
 * Как показать ошибку загрузки комнаты на PreJoin.
 *
 * `not_found` — встречи нет, кнопка «На главную».
 * `failed` — сеть или сервер, кнопка «Войти ещё раз».
 */
export type RoomLookupErrorKind = 'not_found' | 'failed'

/**
 * Сбой lookup комнаты в meet-client-api.
 *
 * Нельзя трактовать как неверный код организатора: PIN при этом мог быть верным.
 */
export class RoomLookupError extends Error {
  statusCode: number
  code: RoomLookupErrorCode

  constructor(message: string, statusCode: number, code: RoomLookupErrorCode) {
    super(message)
    this.name = 'RoomLookupError'
    this.statusCode = statusCode
    this.code = code
  }
}

/**
 * Собирает ошибку lookup из HTTP-ответа ретранслятора.
 *
 * 404 / ROOM_NOT_FOUND — комнаты нет. Остальные неуспехи — ROOM_LOOKUP_FAILED.
 */
export function roomLookupErrorFromResponse(
  status: number,
  body?: { code?: string; message?: string } | null,
): RoomLookupError {
  const isNotFound = body?.code === ROOM_LOOKUP_ERROR_CODE.ROOM_NOT_FOUND || status === 404
  const code = isNotFound
    ? ROOM_LOOKUP_ERROR_CODE.ROOM_NOT_FOUND
    : ROOM_LOOKUP_ERROR_CODE.ROOM_LOOKUP_FAILED
  const statusCode = isNotFound ? 404 : status >= 400 ? status : 500
  const message =
    body?.message?.trim() || (isNotFound ? 'Room not found' : 'Failed to load room')

  return new RoomLookupError(message, statusCode, code)
}

/**
 * Достаёт код lookup из ошибки, в том числе без `instanceof`.
 *
 * В Edge middleware класс может приехать из другого бандла, и `instanceof` молчит.
 */
export function getRoomLookupErrorCode(error: unknown): RoomLookupErrorCode | null {
  if (error instanceof RoomLookupError) {
    return error.code
  }

  if (!error || typeof error !== 'object') {
    return null
  }

  const candidate = error as { name?: unknown; code?: unknown }

  if (candidate.name !== 'RoomLookupError' || typeof candidate.code !== 'string') {
    return null
  }

  if (
    candidate.code === ROOM_LOOKUP_ERROR_CODE.ROOM_NOT_FOUND ||
    candidate.code === ROOM_LOOKUP_ERROR_CODE.ROOM_LOOKUP_FAILED
  ) {
    return candidate.code
  }

  return null
}

/**
 * Переводит код lookup в вид экрана на PreJoin.
 */
export function roomLookupErrorKindFromCode(
  code: RoomLookupErrorCode | null,
): RoomLookupErrorKind | null {
  if (code === ROOM_LOOKUP_ERROR_CODE.ROOM_NOT_FOUND) {
    return 'not_found'
  }

  if (code === ROOM_LOOKUP_ERROR_CODE.ROOM_LOOKUP_FAILED) {
    return 'failed'
  }

  return null
}

/**
 * Сбой проверки кода из-за сети или сервера, а не из-за неверного PIN.
 *
 * 400 — ошибка ввода. 404 / ROOM_NOT_FOUND — комнаты нет, это другой экран.
 */
export function isOrganizerCodeConnectionFailure(status: number, code?: string): boolean {
  if (code === ROOM_LOOKUP_ERROR_CODE.ROOM_LOOKUP_FAILED) {
    return true
  }

  if (code === ROOM_LOOKUP_ERROR_CODE.ROOM_NOT_FOUND) {
    return false
  }

  return status >= 500
}

/**
 * Ответ check_access_rights, который значит «комнаты нет», а не «неверный PIN».
 */
export function isOrganizerCodeRoomNotFound(status: number, code?: string): boolean {
  return code === ROOM_LOOKUP_ERROR_CODE.ROOM_NOT_FOUND || status === 404
}

/**
 * Переводит HTTP-сбой проверки кода в действие на PreJoin.
 *
 * 404 и `ROOM_NOT_FOUND` открывают экран «комнаты нет». Сеть и 5xx — экран
 * соединения. 400 и прочий ввод остаются инлайн «Неверный код».
 */
export function mapOrganizerCodeHttpFailure(
  status: number,
  code?: string,
): 'invalid' | 'connection' | 'not_found' {
  if (isOrganizerCodeRoomNotFound(status, code)) {
    return 'not_found'
  }

  if (isOrganizerCodeConnectionFailure(status, code)) {
    return 'connection'
  }

  return 'invalid'
}
